import { ComputeFunctionType } from 'features/libra/stepComponents/conditionalForm/computed/computeFunctionTypes'
import { z } from 'zod'

export const WeightedParameterSchema = z.object({
  field: z.string(),
  weight: z.number(),
  inverted: z.boolean().optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
})

export type WeightedParameter = z.infer<typeof WeightedParameterSchema>

export const TriStateParameterSchema = z.object({
  field: z.string(),
  type: z.literal('tri-state'),
  defaultValue: z.enum(['low', 'med', 'high']).optional(),
})

export type TriStateParameter = z.infer<typeof TriStateParameterSchema>

export const FunctionSpecSchema = z.object({
  threshold: z.number().optional(),
  parameters: z
    .array(z.union([WeightedParameterSchema, TriStateParameterSchema]))
    .default([]),
})

export type FunctionSpec = z.infer<typeof FunctionSpecSchema>

export const ComputedSectionPresentationSchema = z.object({
  variant: z.literal('eligibility-gate'),
  trueLabel: z.string().default('Proceed'),
  falseLabel: z.string().default('Not eligible'),
  incompleteLabel: z.string().default('Incomplete'),
  triggerValue: z.boolean().default(true),
  hideScoring: z.boolean().default(true),
})

export const OverrideValueMapSchema = z
  .object({
    true: z.string().min(1),
    false: z.string().min(1),
  })
  .refine((map) => map.true !== map.false, {
    message: 'overrideValueMap true and false must be different strings',
    path: ['false'],
  })

export type OverrideValueMap = z.infer<typeof OverrideValueMapSchema>

export const ComputedSectionObjectSchema = z.object({
  id: z.string(),
  type: z.literal('computed'),
  title: z.string().optional(),
  fieldId: z.string().optional(),
  function: z.nativeEnum(ComputeFunctionType),
  /**
   * When true, no computed result is produced until every function parameter
   * has an explicit value (or a configured default).
   */
  requireAllParameters: z.boolean().optional(),
  allowOverride: z.boolean().optional(),
  overrideField: z.string().optional(),
  overrideJustificationField: z.string().optional(),
  overrideActorField: z.string().optional(),
  overrideTimestampField: z.string().optional(),
  /**
   * Stores the questionnaire answers used for a manual decision. If those
   * answers change, the previous manual decision no longer applies and its
   * configured fields are cleared on the next save. Required when
   * `overrideValueMap` is set so a copied enum cannot look like an override.
   */
  overrideParameterSnapshotField: z.string().optional(),
  /**
   * When set, `overrideField` stores these enum strings instead of a boolean,
   * and `fieldId` still receives the system result — not the overridden one.
   */
  overrideValueMap: OverrideValueMapSchema.optional(),
  overridePermissions: z.array(z.string()).optional(),
  presentation: ComputedSectionPresentationSchema.optional(),
  displayFields: z.array(z.string()).default([]),
  functionSpec: FunctionSpecSchema,
})

export type ComputedSection = z.infer<typeof ComputedSectionObjectSchema>

/**
 * A mapped override needs its snapshot field so a copied enum is not mistaken
 * for a live override. `z.union` reports a member's `superRefine` failure as a
 * generic `invalid_union`, so parent config schemas re-run this on their parsed
 * sections to keep the message visible.
 */
export function addComputedSectionSnapshotIssue(
  section: unknown,
  ctx: z.RefinementCtx,
  path: ReadonlyArray<string | number> = []
): void {
  if (!isComputedSection(section)) return
  if (section.overrideValueMap && !section.overrideParameterSnapshotField) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'overrideParameterSnapshotField is required when overrideValueMap is set',
      path: [...path, 'overrideParameterSnapshotField'],
    })
  }
}

export const ComputedSectionSchema = ComputedSectionObjectSchema.superRefine(
  (section, ctx) => addComputedSectionSnapshotIssue(section, ctx)
)

export function getComputedFieldId(section: ComputedSection): string {
  return section.fieldId || section.id
}

export function isComputedSection(
  section: unknown
): section is ComputedSection {
  return (
    typeof section === 'object' &&
    section !== null &&
    'type' in section &&
    section.type === 'computed'
  )
}

/** How a computed section names its true and false results. */
export function getComputedOutcomeLabels(config: ComputedSection): {
  trueLabel: string
  falseLabel: string
} {
  return {
    trueLabel: config.presentation?.trueLabel ?? 'Pass',
    falseLabel: config.presentation?.falseLabel ?? 'Fail',
  }
}
